import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { EmailVerificationBanner } from './EmailVerificationBanner';

export function Layout({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  const { currentUser, userProfile, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="app-shell">
      <div className={`app-frame${wide ? ' app-frame--wide' : ''}`}>
        {/* Topbar */}
        <nav className="topbar">
          <div className="topbar-group topbar-group--left">
            <Link to="/" className="brand-mark">
              <img src="/images/grid-icon.png" alt="GRID" className="brand-mark__img" />
              <span className="brand-mark__name">GRID</span>
            </Link>
            {!currentUser && (
              <>
                <Link to="/affiliate-program" className={`topbar-link${isActive('/affiliate-program') ? ' topbar-link--active' : ''}`}>
                  Affiliate Program
                </Link>
                <Link to="/affiliates" className={`topbar-link${isActive('/affiliates') ? ' topbar-link--active' : ''}`}>
                  Affiliates
                </Link>
              </>
            )}
            {currentUser && (
              <>
                <Link to="/tickets" className={`topbar-link${isActive('/tickets') ? ' topbar-link--active' : ''}`}>
                  My Tickets
                </Link>
                <Link to="/affiliate-program" className={`topbar-link${isActive('/affiliate-program') ? ' topbar-link--active' : ''}`}>
                  Affiliate Program
                </Link>
                <Link to="/affiliates" className={`topbar-link${isActive('/affiliates') ? ' topbar-link--active' : ''}`}>
                  Affiliates
                </Link>
                {isAdmin && (
                  <Link to="/admin/tickets" className={`topbar-link${isActive('/admin') ? ' topbar-link--active' : ''}`}>
                    Admin
                  </Link>
                )}
              </>
            )}
          </div>

          <div className="topbar-group topbar-group--right">
            {currentUser ? (
              <>
                <span className="status-chip">
                  {userProfile?.displayName || currentUser.email?.split('@')[0]}
                  {isAdmin && <strong> · Admin</strong>}
                </span>
                <button className="header-connect" onClick={handleSignOut}>
                  Sign Out
                </button>
              </>
            ) : (
              <Link to="/login" className="header-connect">
                Sign In
              </Link>
            )}
          </div>
        </nav>

        {/* Admin Sub-Nav — shown only when admin is on an /admin/* route */}
        {currentUser && isAdmin && location.pathname.startsWith('/admin') && (
          <nav style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            borderBottom: '1px solid var(--border)',
            padding: '8px 0',
            flexWrap: 'wrap',
          }}>
            {[
              { path: '/admin/tickets', label: 'Tickets' },
              { path: '/admin/affiliates', label: 'Affiliates' },
              { path: '/admin/sprint', label: 'Sprint' },
              { path: '/admin/crm', label: 'CRM' },
              { path: '/admin/notes', label: 'Notes' },
              { path: '/admin/calendar', label: 'Calendar' },
              { path: '/admin/settings', label: 'Settings' },
            ].map(({ path, label }) => (
              <Link
                key={path}
                to={path}
                className={`topbar-link${isActive(path) ? ' topbar-link--active' : ''}`}
              >
                {label}
              </Link>
            ))}
          </nav>
        )}

        {/* Main Content */}
        <main>
          <EmailVerificationBanner />
          {children}
        </main>

        {/* Footer */}
        <footer style={{
          borderTop: '1px solid var(--border)',
          paddingTop: '20px',
          marginTop: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          fontFamily: 'var(--font-mono)',
          fontSize: '10px',
          color: 'var(--text-3)',
          letterSpacing: '0.1em',
        }}>
          <span>© {new Date().getFullYear()} GRID Services · GRID LLC — All rights reserved</span>
          <span style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Link to="/affiliate-program" className="footer-link">Affiliate Program</Link>
            <Link to="/privacy" className="footer-link">Privacy</Link>
            <Link to="/terms" className="footer-link">Terms</Link>
            <Link to="/affiliate-terms" className="footer-link">Affiliate Agreement</Link>
          </span>
        </footer>
      </div>
    </div>
  );
}


